import type {SeriesOptionsType} from 'highcharts';
import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {escapeHtml, formatValue} from '../render/format';
import {CubeMember, MarkRenderer} from '../types';
import {pathChannel, sizeMeasure, sizeOf} from './hierarchy';

interface Node {
    id: string;
    name: string;
    column: number;
    color: string;
    order: number;
}

const renderSankey: MarkRenderer = (cube, spec, ctx) => {
    const index = new CubeIndex(cube);
    const dimensionIds = pathChannel(cube, spec);
    const measure = sizeMeasure(cube);
    const positions = dimensionIds.map(id => index.dimensionIndex(id));
    const nodes = new Map<string, Node>();
    const links = new Map<string, {from: Node; to: Node; weight: number}>();

    const nodeFor = (level: number, member: CubeMember, memberIndex: number): Node => {
        const id = `${dimensionIds[level]}:${member.key}`;
        let node = nodes.get(id);
        if (!node) {
            // Level shift keeps neighbouring columns off the same palette colour; member index keeps it independent of cell order
            node = {
                id,
                name: escapeHtml(member.label),
                column: level,
                color: itemColor(member, undefined, memberIndex + level, ctx),
                order: level * 1e6 + memberIndex,
            };
            nodes.set(id, node);
        }
        return node;
    };

    cube.cells.forEach(cell => {
        const memberIndexes = positions.map(position => cell.coords[position]);
        const members = memberIndexes.map((memberIndex, level) => cube.dimensions[positions[level]]?.members[memberIndex]);
        const weight = sizeOf(cell, measure);
        if (dimensionIds.length < 2 || index.isOutsideTotal(cell, positions) || weight <= 0 || members.some(member => !member || member.role === 'total')) {
            return;
        }

        const path = members.map((member, level) => nodeFor(level, member as CubeMember, memberIndexes[level]));
        // Cells sit at full coordinates, so adding every consecutive pair sums the deeper levels into earlier links
        for (let level = 0; level < path.length - 1; level++) {
            const key = `${path[level].id}\u0000${path[level + 1].id}`;
            const link = links.get(key) ?? {from: path[level], to: path[level + 1], weight: 0};
            link.weight += weight;
            links.set(key, link);
        }
    });

    const data = Array.from(links.values()).map(link => ({
        from: link.from.id,
        to: link.to.id,
        weight: link.weight,
        custom: {tooltip: `${link.from.name} → ${link.to.name}: <b>${formatValue(link.weight, measure, ctx)}</b>`},
    }));

    return {
        type: 'highcharts',
        options: {
            chart: {type: 'sankey'},
            title: {text: undefined},
            credits: {enabled: false},
            legend: {enabled: false},
            tooltip: {
                headerFormat: '',
                pointFormat: '{point.custom.tooltip}',
                nodeFormat: '{point.name}: <b>{point.sum}</b>',
            },
            plotOptions: {
                series: {dataLabels: {enabled: spec.options.dataLabels !== false}},
            },
            series: [{
                type: 'sankey',
                id: measure.id,
                name: escapeHtml(measure.label),
                nodes: Array.from(nodes.values()).sort((a, b) => a.order - b.order).map(({id, name, column, color}) => ({id, name, column, color})),
                data,
                dataLabels: {style: {textOutline: 'none'}},
            } as unknown as SeriesOptionsType],
        },
    };
};

export default renderSankey;
